/**
 * Users slice
 */

import { createAction, createSlice } from '@reduxjs/toolkit';
import { User } from '../../store/slices/types';

const initialState = {
  users: [] as User[],
  currentUser: null as User | null,
  loading: false,
  error: null as string | null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
  },
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsers(state, action) {
      // Handle both array response and paginated response
      if (Array.isArray(action.payload)) {
        state.users = action.payload;
      } else if (action.payload?.data) {
        state.users = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            total: action.payload.pagination.total || action.payload.total || 0,
            page: action.payload.pagination.page || action.payload.page || 1,
            limit: action.payload.pagination.limit || action.payload.limit || 10,
          };
        }
      } else {
        state.users = [];
      }
      state.loading = false;
      state.error = null;
    },
    setCurrentUser(state, action) {
      state.currentUser = action.payload;
      state.loading = false;
      state.error = null;
    },
    addUser(state, action) {
      state.users.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateUser(state, action) {
      const index = state.users.findIndex(
        (u) => (u._id || u.id || u.uid) === (action.payload._id || action.payload.id || action.payload.uid)
      );
      if (index !== -1) {
        state.users[index] = action.payload;
      }
      if (state.currentUser && (state.currentUser._id || state.currentUser.id || state.currentUser.uid) === (action.payload._id || action.payload.id || action.payload.uid)) {
        state.currentUser = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeUser(state, action) {
      state.users = state.users.filter(
        (u) => (u._id || u.id || u.uid) !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
});

// Reducer
export const usersReducer = usersSlice.reducer;

// Actions
export const {
  setUsers,
  setCurrentUser,
  addUser,
  updateUser,
  removeUser,
  setLoading,
  setError,
  clearError,
} = usersSlice.actions;

// Async Actions
export const fetchUsers = createAction<{ page?: number; limit?: number } | undefined>('USERS/FETCH_ALL');
export const fetchUser = createAction<string>('USERS/FETCH_ONE');
export const createUser = createAction<Omit<User, '_id' | 'id' | 'uid'>>('USERS/CREATE');
export const updateUserAction = createAction<{ id: string; data: Partial<User> }>('USERS/UPDATE');
export const deleteUser = createAction<string>('USERS/DELETE');

